import { Box, Button, Flex, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { t } from "i18next";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useListBidsCreatedByDepartmentQuery } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { urls } from "../../../urls";
import { BidInvitationStatus } from "./BidInvitationStatus";
import { Created } from "./CreatedBy";
import { DeleteDraftButton } from "./DeleteDraftButton";
import { EditBidDraftButton } from "./EditBidDraftButton";
import { ViewEventButton } from "./ViewEventButton";

export const DeliveredBidsTable = () => {
  const navigate = useNavigate();
  const confirmationModal = useConfirmationModal();
  const authState = useLoggedInWithOrgContextState();
  const { data, error, isLoading, refetch } = useListBidsCreatedByDepartmentQuery({
    depId: authState.selectedOrg.departments[0].id,
  });
  const displayer = useApiError();

  if (isLoading) return <div>{t("Loading")}...</div>;
  if (error) return displayer.show(error);
  if (!data) return <Box>{t("Sorry, something went wrong")}...</Box>;

  return (
    <>
      {confirmationModal.modal}
      <Box py="5">
        <Text fontSize="medium" color="smMuted">
          {t("Overview of your delivered bids")}
        </Text>
      </Box>
      <Box border="1px solid" borderColor="smBorder" rounded="md" overflowX="auto">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>{t("Event")}</Th>
              <Th>{t("Buyer")}</Th>
              <Th>{t("Created by")}</Th>
              <Th minWidth="240px">{t("Status")}</Th>
              <Th>{t("Actions")}</Th>
            </Tr>
          </Thead>
          <Tbody>
            <>
              {data.bids.map((bid) => {
                return (
                  <Tr key={bid.id}>
                    <Td width={"80"} maxWidth={"80"} overflow={"hidden"} textAlign={"left"}>
                      <Button
                        variant={"link"}
                        colorScheme={"teal"}
                        textAlign={"left"}
                        whiteSpace={"pre-wrap"}
                        onClick={() =>
                          navigate(
                            urls.events.supplierView.go({
                              eventId: bid.forEvent.id,
                              negotiationRoundId: bid.forNegotiationRoundId,
                            })
                          )
                        }
                      >
                        {bid.forEvent.title}
                      </Button>
                    </Td>
                    <Td>
                      <Text>{bid.forEvent.owningOrganizationName}</Text>
                    </Td>
                    <Td>
                      <Created person={bid.createdBy} createdAt={moment(bid.createdAt)} />
                    </Td>
                    <Td>
                      <BidInvitationStatus status={bid.status} />
                      {bid.forNegotiationRound && (
                        <Text pt="1" fontSize="xs" fontWeight="semibold" color="smSecondary">
                          {t("Negotiation round")} {bid.forNegotiationRound}
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <Flex flexDirection={"row"}>
                        {bid.status === "DraftCreated" ? (
                          <>
                            <EditBidDraftButton bid={bid} colorScheme={"teal"} size="sm" />
                            <Box w="5px"></Box>
                            <DeleteDraftButton bid={bid} refetch={refetch} />
                          </>
                        ) : (
                          <ViewEventButton bid={bid} />
                        )}
                      </Flex>
                    </Td>
                  </Tr>
                );
              })}
              {data.bids.length === 0 && (
                <Tr>
                  <Td colSpan={4} width="full" textAlign="center">
                    {t("Your organization has not delivered any bids, yet!")}
                  </Td>
                </Tr>
              )}
            </>
          </Tbody>
        </Table>
      </Box>
    </>
  );
};
